/**
 * AppLogo Component
 *
 * Simple spinning circle with "B" letter
 * Used in mobile header alongside app name
 */

import React from 'react';

export interface AppLogoProps {
  /**
   * Size of the logo (width and height)
   */
  size?: number;
  /**
   * Whether to show the spinning animation
   */
  animate?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * App Logo - Simple spinning circle with "B" inside
 *
 * @example
 * ```tsx
 * <AppLogo size={32} animate />
 * ```
 */
export const AppLogo: React.FC<AppLogoProps> = ({
  size = 32,
  animate = true,
  className = '',
}) => {
  return (
    <div className={`inline-flex items-center justify-center ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Gradient definitions */}
        <defs>
          <linearGradient id="circleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#06b6d4', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#0891b2', stopOpacity: 1 }} />
          </linearGradient>
        </defs>

        {/* Outer spinning circle */}
        <circle
          cx="50"
          cy="50"
          r="48"
          fill="none"
          stroke="url(#circleGradient)"
          strokeWidth="3"
          strokeDasharray="80 220"
          strokeLinecap="round"
          className={animate ? 'animate-spin-slow origin-center' : ''}
          style={{ transformOrigin: 'center' }}
        />

        {/* Inner solid circle background */}
        <circle cx="50" cy="50" r="38" fill="#0891b2" />

        {/* Letter "B" in the center */}
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontSize="40"
          fontWeight="bold"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          B
        </text>
      </svg>
    </div>
  );
};
